import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scale, Trophy, Hourglass } from 'lucide-react';
import { formatKickoff, formatCountdown } from '../../lib/formatDate';
import * as splitPrizeApi from '../../api/splitPrize';
import './gameDetails.css';

/**
 * The last two standing agreeing to share the prize rather than play it out.
 *
 * Only ever shown to those two. Everyone else - eliminated players, the manager
 * watching on - has nothing to decide here, and a panel describing someone else's
 * negotiation is just noise on their page.
 *
 * The states are asymmetric on purpose, because the two players are not in the
 * same position:
 *  - the one who offered can only wait; there is nothing for them to press
 *  - the one who received it accepts or rejects, and the server enforces that
 *    they are the only one who can
 *
 * **This is a deal between two people, not a request and a response, and the panel now
 * says so.** Offering used to leave the screen sitting on "Waiting on them" for ever:
 * nothing refreshed, so a player who had already accepted or turned it down showed as
 * still deciding until the page was reloaded by hand. It read as a hang, and there was
 * no way to tell a hang from a mate who had put their phone down. So:
 *
 *  - it polls while there is anything live to watch, and stops the moment there is not.
 *    A resolution reaches the other side within a few seconds without anybody reloading.
 *  - it says out loud that it is waiting on a person, and gives the deadline the offer
 *    lapses at, so "nothing is happening" and "they have not looked yet" are different
 *    sentences on the screen rather than the same silence.
 *  - accepting shows its own terminal state instead of handing straight back to a
 *    page-wide reload. Ending a game takes the server a moment - the league's next
 *    cycle is built off the back of it - and the old flow spent that moment showing a
 *    full-page "Loading game..." spinner over the top of the decision just made.
 *
 * An offer is good for the current game week only. Once picks lock it expires
 * unanswered, and after a rejection neither player can offer again until the next
 * week - so a turned-down offer says when it can be raised again rather than
 * leaving a dead button.
 */

// Slow enough to be free at the scale of two players on one page, fast enough that an
// answer feels immediate to whoever is watching for it.
const POLL_MS = 8000;

function SplitPrizePanel({ game, currentUser, pickDeadline, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState(null);
  const [error, setError] = useState('');

  const gameId = game?.id;
  const remaining = game?.remainingPlayers || [];
  // The offer is between the final two. Anything else and there is nothing to show,
  // so the panel does not even ask the server.
  const isFinalTwo = remaining.length === 2;
  const amRemaining = remaining.some((p) => p.id === currentUser?.id);
  const applies = Boolean(gameId) && isFinalTwo && amRemaining;

  // The page's own refresh, held in a ref. It is a fresh closure on every render of
  // the parent, and depending on it directly would tear down and rebuild the poll
  // timer on every one of those renders - including the ones polling itself causes.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  /**
   * Returns what it read as well as storing it. The caller needs the answer in the same
   * turn to decide whether the negotiation is over, and reading it back out of state
   * would be reading the value from before this call.
   */
  const load = useCallback(async () => {
    if (!applies) return [];
    try {
      const [all, can] = await Promise.all([
        splitPrizeApi.getSplitRequests(gameId),
        splitPrizeApi.canRequestSplit(gameId),
      ]);
      setRequests(all || []);
      setCanRequest(Boolean(can?.canRequest));
      return all || [];
    } catch {
      // A failed read leaves the panel hidden rather than showing buttons whose
      // state we do not actually know.
      setRequests([]);
      setCanRequest(false);
      return [];
    } finally {
      setLoaded(true);
    }
  }, [applies, gameId]);

  useEffect(() => {
    load();
  }, [load, game?.gameWeek]);

  // What the poll is watching. A string so the effect below restarts when an offer
  // appears or is answered, and not when an identical list is simply re-fetched.
  const outstanding = requests
    .filter((r) => r.status === 'PENDING')
    .map((r) => r.id)
    .join(',');

  /**
   * Watch for the other player answering.
   *
   * Only while an offer is actually outstanding: a panel offering to make one has
   * nothing to wait for, and polling a page nobody is negotiating on is pure noise.
   * Paused while the tab is hidden, because a phone in a pocket is the commonest way
   * for this screen to be left open for an hour.
   */
  useEffect(() => {
    if (!applies || !outstanding) return undefined;

    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      const rows = await load();
      // Resolved by them: the game itself has changed - it may well be over - so the
      // page around this panel needs to hear about it too.
      if (!rows.some((r) => r.status === 'PENDING') && onChangedRef.current) {
        onChangedRef.current();
      }
    };

    const timer = setInterval(tick, POLL_MS);
    // A tab coming back to the front is the moment somebody is most likely to be
    // looking for an answer, so ask then rather than waiting out the interval.
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [applies, load, outstanding]);

  /**
   * Run an action, then re-read.
   *
   * `onChanged` refreshes the page around the panel and is deliberately not awaited
   * before the panel updates itself: the decision is made and the panel should say so
   * at once, rather than after every fixture and pick on the page has been re-fetched.
   */
  const run = async (action, { confirmText, settledTo } = {}) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError('');
    setBusy(true);
    try {
      await action();
      if (settledTo) setSettled(settledTo);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.body?.error || 'Could not update the split offer');
      setSettled(null);
    } finally {
      setBusy(false);
    }
  };

  if (!applies || !loaded) return null;

  const pending = requests.find((r) => r.status === 'PENDING');

  // The handle, because that is what the roster carries: GET /api/games/{id} is permitAll and
  // stopped sending real names when it stopped sending the Player entity. The fallback is for a
  // roster of two that somehow does not include you, which the `applies` guard already rules out.
  const theirName = () => {
    const other = remaining.find((p) => p.id !== currentUser?.id);
    return other?.displayName || 'the other player';
  };

  // What the offer lapses at, if the page knows. Worth saying either way, but a real
  // time turns "before picks lock" from a rule into something you can plan around.
  const lapses = pickDeadline
    ? `It lapses ${formatCountdown(pickDeadline)} (${formatKickoff(pickDeadline)}), when picks lock.`
    : 'It lapses when picks lock.';

  // Accepted here, in this session. The game is over and the page behind this panel is
  // still catching up, so say the outcome rather than showing a spinner over it.
  if (settled === 'ACCEPTED') {
    return (
      <Panel error={error}>
        <p className="sp-title">
          <Trophy size={16} className="sp-title-icon" />
          Split agreed — you both win
        </p>
        <p className="sp-body">
          That is the game over. The result is recorded; the rest of this page is still
          catching up and will show it in a moment.
        </p>
      </Panel>
    );
  }

  // Offer outstanding, and it is yours to answer.
  if (pending && pending.otherPlayerId === currentUser?.id) {
    return (
      <Panel error={error}>
        <p className="sp-title">{pending.requestingPlayerName} wants to split the prize</p>
        <p className="sp-body">
          Accept and the game ends here as a shared win for both of you. Turn it down
          and you play on.
        </p>
        <p className="sp-body">
          {`If you do neither, the offer lapses and the game carries on as normal. ${lapses}`}
        </p>
        <div className="sp-actions">
          {/* The only irreversible button on the panel, so the only one that asks
              twice. Rejecting just continues the game, and offering can be turned
              down - neither needs a gate. */}
          <button
            className="sp-btn is-accept"
            disabled={busy}
            onClick={() =>
              run(() => splitPrizeApi.acceptSplit(gameId, pending.id), {
                confirmText: 'Accept the split? The game ends now and you both share the prize.',
                settledTo: 'ACCEPTED',
              })
            }
          >
            {busy ? 'Ending the game...' : 'Accept and share'}
          </button>
          <button
            className="sp-btn is-reject"
            disabled={busy}
            onClick={() => run(() => splitPrizeApi.rejectSplit(gameId, pending.id))}
          >
            Play on
          </button>
        </div>
      </Panel>
    );
  }

  // Offer outstanding, and it is yours - nothing to press.
  if (pending) {
    return (
      <Panel error={error}>
        <p className="sp-title">
          <Hourglass size={16} className="sp-title-icon" />
          Waiting on {pending.otherPlayerName}
        </p>
        <p className="sp-body">
          Your offer has been sent. It is theirs to accept or turn down — and they may
          not have looked yet, so there is nothing to do but wait.
        </p>
        <p className="sp-body">
          {`This page is watching for their answer and will update on its own. ${lapses}`}
        </p>
        <p className="sp-waiting" aria-live="polite">
          Nothing has changed yet.
        </p>
      </Panel>
    );
  }

  if (canRequest) {
    return (
      <Panel error={error}>
        <p className="sp-title">Split the prize?</p>
        <p className="sp-body">
          It is just you and {theirName()} left. Offer to share it and the game ends
          as a shared win — if they agree.
        </p>
        <p className="sp-body">
          {`They answer in their own time, so expect to wait. ${lapses}`}
        </p>
        <div className="sp-actions">
          <button
            className="sp-btn is-offer"
            disabled={busy}
            onClick={() => run(() => splitPrizeApi.requestSplit(gameId))}
          >
            <Scale size={16} />
            {busy ? 'Sending...' : 'Offer to split'}
          </button>
        </div>
      </Panel>
    );
  }

  // Cannot offer, and the last thing that happened was a rejection. The server
  // clears the per-week block when an offer expires unanswered, so a rejection is
  // the only thing that can be holding it - which makes "next week" accurate
  // rather than a guess.
  const latest = [...requests].sort((a, b) =>
    String(b.requestedAt).localeCompare(String(a.requestedAt))
  )[0];

  if (latest?.status === 'REJECTED') {
    return (
      <Panel error={error}>
        <p className="sp-title">Playing on</p>
        <p className="sp-body">
          A split was turned down this week. Either of you can offer again next week
          if you are both still standing.
        </p>
      </Panel>
    );
  }

  return null;
}

function Panel({ children, error }) {
  return (
    <div className="sp-panel">
      <div className="sp-main">
        {children}
        {error && <p className="sp-error">{error}</p>}
      </div>
    </div>
  );
}


export default SplitPrizePanel;
